// Real cart/variant wiring for the pavoi-lobe-lifters tier picker.
// product-launches/shared/interactions.js's initTierSelector() only handles
// the presentation layer (price tokens, active-card styling) by design --
// this file is the "CC dev" half: making Add to Cart actually add the
// variant the visitor selected. Follows the cc-page-clone skill's P0 ATC
// pattern verified in production on the SuperBalance/Berberine/etc. builds
// (2026-08-28 incident): a native <form action="/cart/add"> submission,
// never fetch('/cart/add.js'); a fresh click and a same-tier repeat click
// make zero AJAX cart calls; only a genuine tier switch makes one
// /cart/clear.js call (retried on failure) before the same form-post add.
//
// Two real ATC entry points exist in this captured markup, both pointing
// at the same form via a form= attribute: the primary <buy-buttons> block
// and a separate sticky-bar copy. The primary one's own <button> has no
// form= attribute of its own -- only the wrapping <buy-buttons> custom
// element does -- because in the original live theme, Shopify's real
// buy-buttons.liquid web component (not ported here, per the page-clone
// skill's JS-audit step: only page-specific interactions get rebuilt, not
// theme-internal Shopify components) reads that attribute itself and
// submits the form programmatically on click. Without that component
// loaded, clicking it natively does nothing, so both entry points are
// wired here with explicit click handlers rather than relying on the
// form's native submit event -- one shared handler function, called from
// both, so there's still exactly one add-to-cart code path (no double-ATC).
(function () {
  'use strict';

  var VARIANT_MAP = {
    'tier-1': 53924958929203,
    'tier-2': 53924958961971,
    'tier-3': 53924958994739
  };
  var PRODUCT_ID = 10405595185459;
  var TIER_PAIR_COUNT = { 'tier-1': 1, 'tier-2': 2, 'tier-3': 3 };

  // Shipping-protection add-on (Priority Insured Shipping, $4.95) -- added
  // to every order alongside whatever tier the visitor selects, regardless
  // of quantity/tier, same as every other CC-clone product on this store.
  // Submitted as a second `items[1][...]` pair on the SAME form-post -- a
  // real anonymous-cart test against this store confirmed Shopify's
  // /cart/add accepts a bare id/quantity (item 0, this form's existing
  // captured fields) mixed with an items[1][...] pair in one POST, so the
  // existing id/quantity/properties[...] fields below are left completely
  // untouched.
  var SHIPPING_PROTECTION_VARIANT_ID = 53646568128819;

  // Shared across every CC-clone product's own interactions.js (same literal
  // key string on every page) rather than a per-product key -- this is what
  // makes "only one product's items in the cart at once" cross-product: this
  // page can tell whether the cart's currently tracked item belongs to a
  // DIFFERENT product page, not just a different tier of this one. Known
  // limit: this uses localStorage, not sessionStorage -- confirmed live
  // 2026-08-30 that sessionStorage's per-TAB scoping let three tabs each add
  // a different product independently (each tab saw no prior tracked item,
  // so each took the zero-call fresh-add path with nothing to clear).
  // localStorage is shared across every tab on this origin, so the same
  // check now sees what any tab already added. Still not a real-time
  // server-truth check (deliberately -- that would mean a network call on
  // every click, the class of change that caused the 2026-08-28 429 incident
  // on the other CC-clone pages), so a customer manually editing /cart in
  // one tab while another tab is mid-purchase is still a narrow gap, but
  // two-tab browsing (the case that actually broke) is now covered.
  var CART_TRACK_KEY = 'acromActiveCartVariant';

  function getForm() {
    return document.querySelector('form.shopify-product-form');
  }

  function getHiddenIdInput(form) {
    return form ? form.querySelector('input[name="id"]') : null;
  }

  // The captured form's hidden product-id field still points at the
  // original reference product this markup was captured from. Shopify's
  // /cart/add validates that the submitted variant id actually belongs to
  // the submitted product-id -- on a mismatch it still adds the item (it
  // trusts id for that) but silently drops the return_to redirect, landing
  // on /cart instead of /checkout. Keep it in sync alongside id.
  function syncProductIdField(form) {
    var input = form ? form.querySelector('input[name="product-id"]') : null;
    if (input) input.value = PRODUCT_ID;
  }

  // The captured form has no return_to field (it was never wired for real
  // checkout), so a plain POST to /cart/add lands on /cart instead of
  // /checkout -- worse conversion than the skill's proven pattern. Add it
  // once, mechanically, rather than leaving the destination to chance.
  function ensureReturnToCheckout(form) {
    var input = form.querySelector('input[name="return_to"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'return_to';
      form.appendChild(input);
    }
    input.value = '/checkout';
  }

  // Adds (or reuses) the two hidden items[1][...] fields that add the
  // shipping-protection product as a second line item on the same submit,
  // alongside whichever tier variant is already in the form's existing
  // id/quantity fields as item 0.
  function ensureShippingProtectionItem(form) {
    var idInput = form.querySelector('input[name="items[1][id]"]');
    if (!idInput) {
      idInput = document.createElement('input');
      idInput.type = 'hidden';
      idInput.name = 'items[1][id]';
      form.appendChild(idInput);
    }
    idInput.value = SHIPPING_PROTECTION_VARIANT_ID;

    var qtyInput = form.querySelector('input[name="items[1][quantity]"]');
    if (!qtyInput) {
      qtyInput = document.createElement('input');
      qtyInput.type = 'hidden';
      qtyInput.name = 'items[1][quantity]';
      form.appendChild(qtyInput);
    }
    qtyInput.value = '1';
  }

  function getCheckedTierRadio() {
    return document.querySelector('.tier-radio:checked');
  }

  // The markup already ships a fully-built "choose a color for each pair"
  // block using Shopify's native line-item-properties convention
  // (name="properties[Pair N Color]") -- Pair 2/3 rows start `hidden` with
  // their <select> `disabled`, clearly built to be revealed per tier, but
  // nothing ever wired that reveal to the tier picker. This block also
  // isn't a descendant of the real <form> at all (select.form is null,
  // confirmed live) -- it's a sibling elsewhere in the page, so even a
  // shown+enabled select is silently excluded from submission with no
  // error. Fix the same way the sticky ATC button already relies on: the
  // HTML `form=` attribute associates a control with a form regardless of
  // DOM position. Disabled selects are still excluded from submission
  // natively, so this only needs to flip hidden/disabled to match the
  // selected tier's pair count once the form association is in place --
  // the cart/checkout/order then get exactly one real Shopify line-item
  // property per pair actually purchased (Gold/Silver), covering any
  // mixed-color combination within a multi-pair bundle.
  function syncPairColorRows() {
    var form = getForm();
    var formId = form ? form.getAttribute('id') : null;
    var radio = getCheckedTierRadio();
    var pairCount = radio ? (TIER_PAIR_COUNT[radio.value] || 1) : 1;
    document.querySelectorAll('.pair-color-row').forEach(function (row) {
      var pairNum = parseInt(row.getAttribute('data-pair'), 10);
      var select = row.querySelector('select');
      var show = pairNum <= pairCount;
      row.hidden = !show;
      if (select) {
        select.disabled = !show;
        if (formId && !select.hasAttribute('form')) select.setAttribute('form', formId);
      }
    });
  }

  function getSelectedVariantId() {
    var radio = getCheckedTierRadio();
    if (!radio) return null;
    return VARIANT_MAP[radio.value] || null;
  }

  // Keep the form's hidden variant id in sync with whichever tier is
  // checked -- including on first paint, since the page loads with Tier 1
  // pre-checked and a visitor who clicks Add to Cart without ever touching
  // the picker still needs the correct variant, not the placeholder id this
  // markup shipped with.
  function syncHiddenIdField() {
    var form = getForm();
    var input = getHiddenIdInput(form);
    var variantId = getSelectedVariantId();
    if (input && variantId) input.value = variantId;
    syncProductIdField(form);
  }

  function getTrackedVariant() {
    try { return localStorage.getItem(CART_TRACK_KEY); } catch (e) { return null; }
  }
  function setTrackedVariant(id) {
    try { localStorage.setItem(CART_TRACK_KEY, String(id)); } catch (e) { /* private mode etc. */ }
  }

  function fetchWithRetry(url, options, retries, delayMs) {
    return fetch(url, options).then(function (res) {
      if (!res.ok && (res.status === 429 || res.status >= 500) && retries > 0) {
        return new Promise(function (resolve) { setTimeout(resolve, delayMs); })
          .then(function () { return fetchWithRetry(url, options, retries - 1, delayMs * 2); });
      }
      return res;
    }, function (err) {
      if (retries > 0) {
        return new Promise(function (resolve) { setTimeout(resolve, delayMs); })
          .then(function () { return fetchWithRetry(url, options, retries - 1, delayMs * 2); });
      }
      throw err;
    });
  }

  function getAtcButtons(form) {
    if (!form) return [];
    // NOTE: form.id is unreliable here -- this form has an <input name="id">
    // (the variant field), and per the HTML spec a named form control shadows
    // same-named IDL properties on the form element itself ("DOM clobbering"):
    // form.id resolves to that <input> element, not the id attribute string.
    // getAttribute('id') always reads the literal HTML attribute and isn't
    // affected by this. Getting this wrong silently breaks the selector below
    // to match zero buttons -- no listeners attach, and every click then
    // falls through to native form submission with no custom handling
    // (which happened, undetected, until this was traced down).
    var formId = form.getAttribute('id');
    var sel = 'buy-buttons[form="' + formId + '"] button[type=submit], button[form="' + formId + '"]';
    var matched = Array.prototype.slice.call(document.querySelectorAll(sel));
    // Belt and suspenders: also include any submit button that the browser's
    // own native form-association (button.form) resolves back to this form,
    // regardless of how that association happened (nested inside the form,
    // or via a form= attribute) -- covers markup shapes the CSS selector
    // above doesn't anticipate.
    document.querySelectorAll('button[type=submit]').forEach(function (btn) {
      if (btn.form === form && matched.indexOf(btn) === -1) matched.push(btn);
    });
    return matched;
  }

  function setAtcLoading(buttons, loading) {
    buttons.forEach(function (btn) {
      btn.style.pointerEvents = loading ? 'none' : '';
      btn.style.opacity = loading ? '0.6' : '';
      if (loading) {
        btn.dataset.originalHtml = btn.dataset.originalHtml || btn.innerHTML;
        btn.textContent = 'Adding...';
      } else if (btn.dataset.originalHtml) {
        btn.innerHTML = btn.dataset.originalHtml;
      }
    });
  }

  // Restores every ATC button if the browser restores this page from
  // bfcache (Back button from /checkout) instead of a fresh load --
  // otherwise a button left mid-"Adding..." state from the previous visit
  // stays stuck.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    setAtcLoading(getAtcButtons(getForm()), false);
  });

  function handleAddToCart(e) {
    e.preventDefault();
    var form = getForm();
    if (!form) return;
    var buttons = getAtcButtons(form);
    var variantId = getSelectedVariantId();
    if (!variantId) {
      alert('Please select an offer above.');
      return;
    }
    var input = getHiddenIdInput(form);
    if (input) input.value = variantId;
    syncProductIdField(form);
    ensureReturnToCheckout(form);
    ensureShippingProtectionItem(form);

    var tracked = getTrackedVariant();
    if (tracked && Number(tracked) === variantId) {
      // Already in cart this session -- skip straight to checkout, no
      // cart-endpoint calls at all.
      window.location.href = '/checkout';
      return;
    }

    if (!tracked) {
      // Common case: nothing added yet this session. Native form-post --
      // zero AJAX cart calls. return_to already routes to /checkout.
      setTrackedVariant(variantId);
      setAtcLoading(buttons, true);
      form.submit();
      return;
    }

    // Genuine tier switch: clear the previous tier first (one AJAX call,
    // retried on failure), then the native form-post adds the new one.
    setAtcLoading(buttons, true);
    setTrackedVariant(variantId);
    fetchWithRetry('/cart/clear.js', { method: 'POST' }, 2, 1000)
      .then(function () {
        form.submit();
      })
      .catch(function () {
        setAtcLoading(buttons, false);
        alert('Could not update your cart. Please try again.');
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    syncHiddenIdField();
    syncPairColorRows();
    document.querySelectorAll('.tier-radio').forEach(function (radio) {
      radio.addEventListener('change', syncHiddenIdField);
      radio.addEventListener('change', syncPairColorRows);
    });
    getAtcButtons(getForm()).forEach(function (btn) {
      btn.addEventListener('click', handleAddToCart);
    });
  });
})();
